/**
 * Print agent flavour matching (prod vs staging agent on one PC).
 *
 * The prod and staging desktop agents used to share one port list, so the
 * second one to start landed on a fallback port and a web app could talk to
 * the wrong one. From agent 2.0.22 each flavour binds its own port list and
 * reports `flavour` on `/status`. This module holds the pure rules:
 *
 *  - which ports this web app probes (`candidatePortsFor`);
 *  - whether a responding agent is a match, a fallback, or must be ignored
 *    (`classifyAgent`).
 *
 * An agent that reports no flavour (2.0.21 and older) is handled exactly as
 * before: a `backendUrl` match wins, otherwise it is a first-responder
 * fallback. Only an agent that REPORTS the other flavour is ignored.
 */

export type AgentFlavour = 'prod' | 'staging';

// KEEP IN LOCKSTEP with apps/print-agent/src/port-discovery.ts
// (PROD_CANDIDATE_PORTS / STAGING_CANDIDATE_PORTS).
export const PROD_AGENT_PORTS = [58876, 49876, 58877, 58878, 58879] as const;
export const STAGING_AGENT_PORTS = [57876, 50876, 57877, 57878, 57879] as const;
// The v1.4.x bridge port, kept LAST on prod (BLU-410).
const LEGACY_AGENT_PORT = 9876;
// Where a 2.0.21-or-older staging agent sits: the first two ports of the old
// shared list. Older agents never used the rest for staging in practice, and
// 9876 is v1.4.x only (prod fleet), so staging skips them to stay quiet.
const OLD_SHARED_PORTS_FOR_STAGING = [58876, 49876] as const;

/**
 * The flavour of agent THIS web app should talk to, from its baked backend
 * URL. Undefined for local dev with a local (or no) backend: every agent is
 * acceptable there, same as before.
 */
export function expectedAgentFlavour(backendUrl: string | undefined): AgentFlavour | undefined {
  if (!backendUrl) return undefined;
  let host: string;
  try {
    host = new URL(backendUrl).hostname;
  } catch {
    return undefined;
  }
  if (host.includes('staging')) return 'staging';
  if (host === 'localhost' || host === '127.0.0.1' || host === '::1') return undefined;
  return 'prod';
}

/**
 * Ports to probe, in preference order.
 *  - prod: the exact list prod has always probed (unchanged).
 *  - staging: the new staging ports, then 58876 and 49876, where a
 *    2.0.21-or-older staging agent may still sit (a prod agent there is
 *    ignored by flavour).
 *  - unknown (dev): everything.
 */
export function candidatePortsFor(flavour: AgentFlavour | undefined): readonly number[] {
  if (flavour === 'prod') return [...PROD_AGENT_PORTS, LEGACY_AGENT_PORT];
  if (flavour === 'staging') return [...STAGING_AGENT_PORTS, ...OLD_SHARED_PORTS_FOR_STAGING];
  return [...STAGING_AGENT_PORTS, ...PROD_AGENT_PORTS, LEGACY_AGENT_PORT];
}

export interface AgentIdentity {
  port: number;
  flavour: AgentFlavour | undefined;
  backendUrl: string | undefined;
}

/**
 * - `match`: the right agent for this web app; use it straight away.
 * - `fallback`: acceptable if nothing matches (an older agent that does not
 *   say which flavour it is, same rule as before).
 * - `reject`: says it is the other flavour; never use it.
 */
export type AgentFit = 'match' | 'fallback' | 'reject';

export function classifyAgent(
  agent: Pick<AgentIdentity, 'flavour' | 'backendUrl'>,
  expected: { flavour: AgentFlavour | undefined; backendUrl: string | undefined },
): AgentFit {
  if (agent.flavour !== undefined && expected.flavour !== undefined) {
    return agent.flavour === expected.flavour ? 'match' : 'reject';
  }
  if (expected.backendUrl && agent.backendUrl === expected.backendUrl) return 'match';
  return 'fallback';
}

/** Operator wording for a skipped agent; shared by the console and the settings notice. */
export function ignoredAgentMessage(agent: AgentIdentity, siteFlavour: AgentFlavour | undefined): string {
  return `A ${agent.flavour} print agent is running on this PC; this ${siteFlavour} site ignores it.`;
}

const loggedIgnored = new Set<string>();

/**
 * One console.info per ignored agent per page load (rescans would otherwise
 * repeat it every few seconds).
 */
export function logIgnoredAgent(agent: AgentIdentity, siteFlavour: AgentFlavour | undefined): void {
  const key = `${agent.flavour}:${agent.port}`;
  if (loggedIgnored.has(key)) return;
  loggedIgnored.add(key);
  console.info(`[print-agent] ${ignoredAgentMessage(agent, siteFlavour)} (port ${agent.port})`);
}

/** Tests only: forget which agents were already logged. */
export function resetIgnoredAgentLog(): void {
  loggedIgnored.clear();
}

/** Read a `/status` `flavour` field; anything else is "not reported". */
export function parseAgentFlavour(raw: unknown): AgentFlavour | undefined {
  return raw === 'prod' || raw === 'staging' ? raw : undefined;
}
